import React, { useState, useEffect } from 'react';
import { loadLibrary, subscribeLibrary } from '../marketing-v2/services/imageLibrary';
import ColourPicker from '../shared/ColourPicker';
import AbstractScene from './AbstractScene';
import ShapeRenderer from './ShapeRenderer';
import { TEXT_PRESETS, SHAPE_TYPES, OCCASIONS, FONT_OPTIONS } from './data';

// Small chevron used in the inline colour-trigger rows.
const ChevronRight = () => (
  <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M3 2l3 3-3 3" />
  </svg>
);
const ChevronLeft = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M9 3L5 7l4 4" />
  </svg>
);

// Inline swatch trigger — replaces the shared ColourPicker's trigger
// for embedded use in the side panel. Tapping it routes to the
// sub-screen picker rather than opening a centred modal.
const InlineColourTrigger = ({ value, label, onClick }) => (
  <button type="button" className="v2-cd-colour-trigger" onClick={onClick}>
    <span className="v2-cd-colour-trigger-swatch" style={{ background: value }} />
    <span className="v2-cd-colour-trigger-label">{label}</span>
    <span className="v2-cd-colour-trigger-hex">{(value || '').toUpperCase()}</span>
    <ChevronRight />
  </button>
);

// DesignerCategoryPanel — the side panel that slides out from the rail
// (or rises from the bottom on mobile). The `category` prop selects
// which content renders. Each content block calls the appropriate
// callback on the designer to add an element / set a property.

const TITLES = {
  text: 'Text',
  shapes: 'Shapes',
  amount: 'Amount',
  image: 'Image',
  background: 'Background',
  'edit-font':  'Font',
  'edit-color': 'Colour',
  'edit-stroke': 'Outline colour',
};

// Inline colour editor for the currently-selected element. Same shape
// as the BackgroundPanel sub-screen — full-panel inline ColourPicker
// wired to the element's `color` (or `fill` for shapes).
const SelectionColourPanel = ({ selectedEl, onElementChange, field }) => {
  if (!selectedEl) return <p className="v2-cd-panel-help">Select an element first.</p>;
  // `field` lets the caller target the outline ('stroke'); otherwise edit the
  // element's main colour ('fill' for shapes, 'color' for text/amount).
  const f = field || (selectedEl.type === 'shape' ? 'fill' : 'color');
  const current = selectedEl[f] || (f === 'stroke' ? '#29282D' : '#FFFFFF');
  return (
    <ColourPicker
      value={current}
      onChange={(c) => onElementChange(selectedEl.id, { [f]: c })}
      inline
      paletteAa={false}
    />
  );
};

// Inline font editor — vertical list of font tiles, each rendered in
// its actual typeface. Click commits to the selected element's font.
const SelectionFontPanel = ({ selectedEl, onElementChange }) => {
  if (!selectedEl) return <p className="v2-cd-panel-help">Select an element first.</p>;
  const current = selectedEl.font || 'fraunces';
  return (
    <div className="v2-cd-font-list">
      {FONT_OPTIONS.map(f => (
        <button
          key={f.id}
          type="button"
          className={`v2-cd-font-tile ${current === f.id ? 'is-active' : ''}`}
          style={{ fontFamily: f.family }}
          onClick={() => onElementChange(selectedEl.id, { font: f.id })}
        >
          {f.label}
        </button>
      ))}
    </div>
  );
};

// ─── Sub-panels ───

const TextPanel = ({ onAddText, onAddPresetText }) => (
  <>
    <button
      type="button"
      className="v2-cd-panel-cta"
      onClick={() => onAddText({})}
    >
      Add a text box
    </button>
    <p className="v2-cd-panel-section-label">Default text styles</p>
    <div className="v2-cd-panel-presets">
      {TEXT_PRESETS.map((p) => (
        <button
          key={p.id}
          type="button"
          className="v2-cd-panel-preset"
          onClick={() => onAddPresetText(p)}
          style={{
            fontSize: `${Math.min(p.fontSize, 28)}px`,
            fontWeight: p.bold ? 700 : 500,
            fontStyle: p.italic ? 'italic' : 'normal',
          }}
        >
          {p.label}
        </button>
      ))}
    </div>
  </>
);

const ShapesPanel = ({ onAddShape }) => (
  <div className="v2-cd-panel-shapes">
    {SHAPE_TYPES.map((s) => (
      <button
        key={s.id}
        type="button"
        className="v2-cd-panel-shape"
        onClick={() => onAddShape(s.id)}
        aria-label={s.label}
      >
        <ShapeRenderer shape={s.id} fill="#29282D" />
      </button>
    ))}
  </div>
);

const AmountPanel = ({ onAddAmount, hasAmount, currency }) => (
  <>
    <button
      type="button"
      className="v2-cd-panel-cta"
      onClick={onAddAmount}
      disabled={hasAmount}
    >
      Add the voucher amount
    </button>
    <p className="v2-cd-panel-help">
      {hasAmount
        ? 'An amount is already on the card. Select it to restyle, or delete to add a new one.'
        : `Drops a styled "${currency}50" element on the card. The number is replaced with the actual voucher value at checkout.`}
    </p>
  </>
);

// ImagePanel — three tabs for inserting imagery: the salon's main brand
// photo, their portfolio of work, and a direct upload. Salon and
// Portfolio reuse assets the salon already has elsewhere in the app, so
// they don't need to re-upload anything to drop a familiar image onto
// the card. Each tab falls back to a clean empty state if the data
// source is empty (e.g. salon hasn't uploaded a portfolio yet).
const ImagePanel = ({
  onAddImage,
  onAddImageFromUrl,
  salonImage,
  portfolio = [],
}) => {
  const [tab, setTab] = useState('salon');
  // Images the salon uploaded elsewhere (email designer and so on)
  const [library, setLibrary] = useState(() => loadLibrary());
  useEffect(() => subscribeLibrary(() => setLibrary(loadLibrary())), []);

  // Normalise portfolio entries — different fetch paths in the app have
  // surfaced these with either `imageUrl` or `url`; either is fine.
  const portfolioItems = (portfolio || [])
    .map((p, i) => ({
      key: p._id || p.id || i,
      url: p.imageUrl || p.url,
    }))
    .filter((p) => !!p.url);

  return (
    <>
      <div className="v2-ed-segment" role="tablist" aria-label="Image source">
        {[
          { id: 'salon',     label: 'Salon' },
          { id: 'portfolio', label: 'Portfolio' },
          { id: 'upload',    label: 'Upload' },
        ].map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            className={`v2-ed-segment-btn ${tab === t.id ? 'active' : ''}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="v2-cd-image-tab-body">
        {tab === 'salon' && (
          salonImage ? (
            <>
              <div className="v2-cd-image-grid">
                <button
                  type="button"
                  className="v2-cd-image-tile"
                  onClick={() => onAddImageFromUrl && onAddImageFromUrl(salonImage)}
                  aria-label="Add salon image"
                >
                  <img src={salonImage} alt="" />
                </button>
              </div>
              <p className="v2-cd-panel-help">
                Your main salon photo. Tap to drop it on the card, then drag a
                corner to resize.
              </p>
            </>
          ) : (
            <p className="v2-cd-panel-help">
              No salon photo yet. Set one in your salon profile and it'll show
              up here for one-tap use.
            </p>
          )
        )}

        {tab === 'portfolio' && (
          portfolioItems.length > 0 ? (
            <>
              <div className="v2-cd-image-grid">
                {portfolioItems.map((p) => (
                  <button
                    key={p.key}
                    type="button"
                    className="v2-cd-image-tile"
                    onClick={() => onAddImageFromUrl && onAddImageFromUrl(p.url)}
                    aria-label="Add portfolio image"
                  >
                    <img src={p.url} alt="" loading="lazy" />
                  </button>
                ))}
              </div>
              <p className="v2-cd-panel-help">
                Tap any image to drop it on the card. Manage your portfolio
                in your profile.
              </p>
            </>
          ) : (
            <p className="v2-cd-panel-help">
              Your portfolio is empty. Add some of your work in your profile
              and it'll be available here for one-tap use.
            </p>
          )
        )}

        {tab === 'upload' && (
          <>
            <label className="v2-cd-panel-cta">
              <input type="file" accept="image/*" hidden onChange={onAddImage} />
              Upload image
            </label>
            <p className="v2-cd-panel-help">
              Drag your logo, photo or brand mark in. Once uploaded, drag a
              corner on the card to resize.
            </p>
            {library.length > 0 && (
              <>
                <p className="v2-cd-panel-help" style={{ marginTop: 16, fontWeight: 600 }}>Your uploads</p>
                <div className="v2-cd-image-grid">
                  {library.map((img) => (
                    <button
                      key={img.url}
                      type="button"
                      className="v2-cd-image-tile"
                      onClick={() => onAddImageFromUrl && onAddImageFromUrl(img.url)}
                      aria-label="Add uploaded image"
                    >
                      <img src={img.url} alt="" loading="lazy" style={/\.(png|svg|gif)(\?|$)/i.test(img.url) ? { objectFit: 'contain' } : undefined} />
                    </button>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </>
  );
};

const BackgroundPanel = ({
  config, onChange, onChangeBgColor, onChangeGradientColor, onUploadPhoto,
}) => {
  // Sub-screen state — when set, the panel shows the inline colour
  // picker instead of the background controls. Avoids the centred
  // modal which would overlap the canvas.
  const [editing, setEditing] = useState(null); // 'solid' | 'from' | 'to' | null

  if (editing) {
    const labelMap = {
      solid: 'Background colour',
      from:  'Gradient start',
      to:    'Gradient end',
    };
    const value = editing === 'solid' ? config.bgColor
                : editing === 'from'  ? config.bgGradient.from
                : config.bgGradient.to;
    const onChangeColor = editing === 'solid'
      ? onChangeBgColor
      : (v) => onChangeGradientColor(editing, v);
    return (
      <div className="v2-cd-subscreen">
        <button
          type="button"
          className="v2-cd-subscreen-back"
          onClick={() => setEditing(null)}
        >
          <ChevronLeft />
          <span>{labelMap[editing]}</span>
        </button>
        <ColourPicker value={value} onChange={onChangeColor} inline paletteAa={false} />
      </div>
    );
  }

  return (
  <>
    <div className="v2-ed-segment v2-ed-segment-4" role="tablist">
      {['solid', 'gradient', 'photo', 'pattern'].map((mode) => (
        <button
          key={mode}
          type="button"
          className={`v2-ed-segment-btn ${config.bgMode === mode ? 'active' : ''}`}
          onClick={() => onChange({ bgMode: mode })}
        >
          {mode[0].toUpperCase() + mode.slice(1)}
        </button>
      ))}
    </div>

    <div className="v2-cd-panel-bg-body">
      {config.bgMode === 'solid' && (
        <InlineColourTrigger
          value={config.bgColor}
          label="Colour"
          onClick={() => setEditing('solid')}
        />
      )}

      {config.bgMode === 'gradient' && (
        <>
          <InlineColourTrigger value={config.bgGradient.from} label="From" onClick={() => setEditing('from')} />
          <InlineColourTrigger value={config.bgGradient.to}   label="To"   onClick={() => setEditing('to')}   />
          <div className="v2-ed-field" style={{ marginTop: 12 }}>
            <label className="v2-ed-label">Angle</label>
            <input
              type="range" min="0" max="360" step="15"
              value={config.bgGradient.angle}
              onChange={(e) => onChange({ bgGradient: { ...config.bgGradient, angle: parseInt(e.target.value, 10) } })}
              className="v2-dz-range"
            />
            <div className="v2-pp-help" style={{ marginTop: 4 }}>{config.bgGradient.angle}°</div>
          </div>
        </>
      )}

      {config.bgMode === 'photo' && (
        <>
          {config.bgPhoto?.url ? (
            <div className="v2-dz-photo-current">
              <img src={config.bgPhoto.url} alt="" className="v2-dz-photo-img" />
              <button
                type="button"
                className="v2-btn-primary v2-dz-photo-replace"
                onClick={() => document.getElementById('dz-photo-input').click()}
              >Replace</button>
              <button
                type="button"
                className="v2-ed-chip-x v2-dz-photo-remove"
                onClick={() => onChange({ bgPhoto: null, bgMode: 'solid' })}
                aria-label="Remove photo"
              >✕</button>
              <input id="dz-photo-input" type="file" accept="image/*" onChange={onUploadPhoto} hidden />
            </div>
          ) : (
            <label className="v2-dz-photo-empty">
              <input type="file" accept="image/*" onChange={onUploadPhoto} hidden />
              <span className="v2-dz-photo-empty-title">Click to upload</span>
              <span className="v2-dz-photo-empty-hint">After upload, drag on the card to pan and scroll to zoom.</span>
            </label>
          )}
          <p className="v2-pp-help" style={{ marginTop: 10 }}>
            Photos are held in memory for now. Backend persistence is in build.
          </p>
        </>
      )}

      {config.bgMode === 'pattern' && (
        <div className="v2-dz-patterns">
          {OCCASIONS.map((o) => {
            const isActive = (config.bgPattern?.occasion || 'birthday') === o.id;
            return (
              <button
                key={o.id}
                type="button"
                className={`v2-dz-pattern ${isActive ? 'active' : ''}`}
                onClick={() => onChange({ bgPattern: { ...config.bgPattern, occasion: o.id } })}
                aria-label={o.name}
              >
                <div className="v2-dz-pattern-thumb">
                  <AbstractScene occasion={o.id} />
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  </>
  );
};

// ─── Wrapper ───
const DesignerCategoryPanel = ({ category, onClose, selectedEl, ...handlers }) => {
  if (!category) return null;

  let body = null;
  if (category === 'text')           body = <TextPanel {...handlers} />;
  else if (category === 'shapes')    body = <ShapesPanel {...handlers} />;
  else if (category === 'amount')    body = <AmountPanel {...handlers} />;
  else if (category === 'image')     body = <ImagePanel  {...handlers} />;
  else if (category === 'background') body = <BackgroundPanel {...handlers} />;
  else if (category === 'edit-color') body = <SelectionColourPanel selectedEl={selectedEl} onElementChange={handlers.onElementChange} />;
  else if (category === 'edit-stroke') body = <SelectionColourPanel selectedEl={selectedEl} onElementChange={handlers.onElementChange} field="stroke" />;
  else if (category === 'edit-font')  body = <SelectionFontPanel  selectedEl={selectedEl} onElementChange={handlers.onElementChange} />;

  // Dynamic title for edit-* categories — varies based on selected
  // element type (text colour vs amount colour vs shape fill).
  let title = TITLES[category];
  if (category === 'edit-color') {
    title = selectedEl?.type === 'shape'  ? 'Fill colour'
          : selectedEl?.type === 'amount' ? 'Amount colour'
          : 'Text colour';
  }

  return (
    <aside className="v2-cd-panel" aria-label={title}>
      <header className="v2-cd-panel-header">
        <h2 className="v2-cd-panel-title">{title}</h2>
        <button
          type="button"
          className="v2-cd-iconbtn"
          onClick={onClose}
          aria-label="Close panel"
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden>
            <path d="M3 3l8 8M11 3l-8 8" />
          </svg>
        </button>
      </header>
      <div className="v2-cd-panel-body">{body}</div>
    </aside>
  );
};

export default DesignerCategoryPanel;
